import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import {
  ArrowLeft, Send, Package, Clock, CheckCircle, XCircle, Briefcase, PenTool
} from 'lucide-react';
import type { Project, DiversSubProject } from '../types';

interface Request {
  id: string;
  created_at: string;
  description: string;
  status: string;
  project: { code: string; name: string; category?: 'production' | 'divers' } | null;
  divers_sub_project_id?: string | null;
  urgency: string;
  target: string;
}

export default function MaterialRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [projects, setProjects] = useState<Project[]>([]);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);
  const [myRequests, setMyRequests] = useState<Request[]>([]);

  // Formulaire
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedSubProject, setSelectedSubProject] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [target, setTarget] = useState<'direction' | 'be'>('direction');
  const [loading, setLoading] = useState(false);

  // Helper: déterminer si le projet sélectionné est de type Divers
  const selectedProjectData = projects.find(p => p.id === selectedProject);
  const isDiversProject = selectedProjectData?.category === 'divers';
  // Pour le formulaire, ne montrer que les sous-chantiers actifs
  const subProjectsForSelected = diversSubProjects.filter(sp => sp.parent_project_id === selectedProject && sp.is_active);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;

    // 1. Projets actifs (avec catégorie)
    const { data: projs } = await supabase
      .from('projects')
      .select('id, code, name, category')
      .eq('is_active', true)
      .order('code');
    if (projs) setProjects(projs as Project[]);

    // 2. Sous-chantiers Divers (tous, pour affichage des demandes existantes + actifs pour le formulaire)
    const { data: subProjs } = await supabase
      .from('divers_sub_projects')
      .select('*')
      .order('divers_number');
    if (subProjs) setDiversSubProjects(subProjs as DiversSubProject[]);

    // 3. Mes demandes récentes (avec catégorie et divers_sub_project_id)
    const { data: reqs } = await supabase
      .from('material_requests')
      .select('*, project:projects(code, name, category), divers_sub_project_id')
      .eq('employee_id', user.id)
      .order('created_at', { ascending: false });
    if (reqs) setMyRequests(reqs as unknown as Request[]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedProject || !description) return;

    // Pour les projets Divers, un sous-chantier doit être sélectionné
    if (isDiversProject && !selectedSubProject) {
      toast.warning("Veuillez sélectionner un sous-chantier pour le projet Divers.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('material_requests').insert({
        employee_id: user.id,
        project_id: selectedProject,
        divers_sub_project_id: isDiversProject ? selectedSubProject : null,
        description: description,
        urgency: urgency,
        target: target,
        status: 'pending'
      });

      if (error) throw error;

      toast.success("Demande envoyée !");
      setDescription('');
      setSelectedSubProject('');
      setUrgency('normal');
      setTarget('direction');
      fetchData(); // Rafraîchir la liste
    } catch (err) {
      toast.error("Erreur lors de l'envoi.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handler pour changement de projet
  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    setSelectedSubProject(''); // Reset sub-project when project changes
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <span style={{color:'#b45309', background:'#fffbeb', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'4px'}}><Clock size={12}/> En attente</span>;
      case 'validated': return <span style={{color:'#15803d', background:'#dcfce7', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'4px'}}><Package size={12}/> Commandé</span>;
      case 'received': return <span style={{color:'#1e40af', background:'#dbeafe', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle size={12}/> Reçu</span>;
      case 'rejected': return <span style={{color:'#b91c1c', background:'#fee2e2', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', display:'flex', alignItems:'center', gap:'4px'}}><XCircle size={12}/> Refusé</span>;
      default: return null;
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom:'80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}><ArrowLeft size={20} color="#64748b" /></button>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Demande Matériel</h1>
      </div>

      <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '30px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
        <h2 style={{ marginTop:0, fontSize:'1.1rem', color:'#2563eb', display:'flex', alignItems:'center', gap:'10px' }}>
          <Package size={20} /> Nouvelle demande
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px', marginTop:'20px' }}>
          <div>
            <label style={{ display:'block', marginBottom:'6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>Chantier concerné</label>
            <select value={selectedProject} onChange={e => handleProjectChange(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem' }} required>
              <option value="">-- Sélectionner un projet --</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
            </select>
          </div>

          {/* Sous-chantier Divers (affiché uniquement pour les projets Divers) */}
          {isDiversProject && subProjectsForSelected.length > 0 && (
            <div>
              <label style={{ display:'block', marginBottom:'6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>Sous-chantier Divers</label>
              <select
                value={selectedSubProject}
                onChange={e => setSelectedSubProject(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', background: '#fffbeb' }}
                required
              >
                <option value="">-- Sélectionner un sous-chantier --</option>
                {subProjectsForSelected.map(sp => (
                  <option key={sp.id} value={sp.id}>{sp.divers_number} - {sp.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={{ display:'block', marginBottom:'6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>Liste du matériel / Besoin</label>
            <textarea 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              placeholder="Ex: 5 boîtes de vis placo, 1 perceuse à percussion..." 
              rows={4}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', fontFamily:'inherit' }} 
              required 
            />
          </div>

          <div>
            <label style={{ display:'block', marginBottom:'6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>Destinataire</label>
            <div style={{ display:'flex', gap:'10px' }}>
              <button
                type="button"
                onClick={() => setTarget('direction')}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: target === 'direction' ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  background: target === 'direction' ? '#fffbeb' : 'white',
                  color: target === 'direction' ? '#b45309' : '#64748b',
                  fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <Briefcase size={18} /> JLO
              </button>
              <button
                type="button"
                onClick={() => setTarget('be')}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: target === 'be' ? '2px solid #8b5cf6' : '1px solid #e2e8f0',
                  background: target === 'be' ? '#f5f3ff' : 'white',
                  color: target === 'be' ? '#6d28d9' : '#64748b',
                  fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                <PenTool size={18} /> GP
              </button>
            </div>
          </div>

          <div>
            <label style={{ display:'block', marginBottom:'6px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>Urgence</label>
            <div style={{ display:'flex', gap:'10px' }}>
              {['low', 'normal', 'high'].map(level => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setUrgency(level)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', border: urgency === level ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    background: urgency === level ? '#eff6ff' : 'white', color: urgency === level ? '#2563eb' : '#64748b', fontWeight:'600', cursor:'pointer'
                  }}
                >
                  {level === 'low' ? 'Faible' : level === 'normal' ? 'Normale' : 'Urgent 🚨'}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} style={{ padding: '14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' }}>
            {loading ? 'Envoi...' : <>Envoyer la demande <Send size={18} /></>}
          </button>
        </form>
      </div>

      <h3 style={{ color: '#475569', marginBottom:'15px' }}>Mes dernières demandes</h3>
      <div style={{ display: 'grid', gap: '12px' }}>
        {myRequests.map(req => {
          // Déterminer le nom à afficher
          let displayCode = req.project?.code || '';
          let displayName = req.project?.name || '';

          // Si c'est un projet Divers avec un sous-chantier sélectionné
          if (req.divers_sub_project_id && req.project?.category === 'divers') {
            const subProj = diversSubProjects.find(sp => sp.id === req.divers_sub_project_id);
            if (subProj) {
              displayCode = subProj.divers_number;
              displayName = subProj.name;
            }
          }

          return (
            <div key={req.id} style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', display:'flex', flexDirection:'column', gap:'8px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight:'700', color:'#1e293b' }}>{displayCode}</span>
                  <span style={{ color:'#64748b', fontSize:'0.85rem' }}>{displayName}</span>
                  {req.target === 'be' ? (
                    <span style={{ fontSize: '0.7rem', fontWeight: '600', background: '#f5f3ff', color: '#6d28d9', padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PenTool size={10} /> GP
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.7rem', fontWeight: '600', background: '#fffbeb', color: '#b45309', padding: '3px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Briefcase size={10} /> JLO
                    </span>
                  )}
                </div>
                {getStatusBadge(req.status)}
              </div>
              <p style={{ margin:0, color:'#475569', fontSize:'0.9rem', whiteSpace:'pre-wrap' }}>{req.description}</p>
              <div style={{ fontSize:'0.8rem', color:'#94a3b8', marginTop:'4px' }}>Demandé le {new Date(req.created_at).toLocaleDateString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}